import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import Spinner from 'react-spinkit';
import { Icon } from 'ui';

const Button = forwardRef(
  (
    {
      testid,
      text = 'Title',
      variant = 'default',
      loading = false,
      onClick,
      to,
      disabled = false,
      icon,
      size = 'default',
      color = 'primary',
      borderRadius = 'default',
      iconSize = 'l',
      onlyIcon,
      iconPosition = 'default',
      iconColor = 'white',
      className,
      type = 'button',
      theme = 'default',
      ariaLabel,
      ariaExpanded,
      ariaControls,
      ariaHasPopup,
      accessibilityOnly = false,
      children,
      title: titleProp,
      ...restProps
    },
    ref
  ) => {
    // Determinar el aria-label: usar el prop ariaLabel si existe,
    // si no usar el texto del botón, especialmente importante para botones con solo icono
    const buttonAriaLabel = ariaLabel || text;
    // Determinar si está deshabilitado
    const isDisabled = disabled || loading;

    // Manejo de eventos de teclado
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (onClick && !isDisabled) {
          onClick(event);
        }
      }
    };

    const hasIconAndText = icon && !onlyIcon && text;
    const buttonClasses = clsx(
      'button',
      `button--${variant}`,
      `button--size-${size}`,
      `button--color-${color}`,
      `button--border-radius-${borderRadius}`,
      `button--icon-position-${iconPosition}`,
      {
        'button--color-only-icon': onlyIcon,
        'button--has-icon-and-text': hasIconAndText,
        'button--icon-only': onlyIcon,
        [`button--theme-${theme}`]: theme,
      },
      className
    );

    // Si es un elemento convertido a botón que mantiene su aspecto original
    const buttonClassesAccessibility = clsx('button-accessibility-only', { 'button--icon-only': onlyIcon }, className);

    // Contenido común para botón y enlace
    const renderContent = () => (
      <>
        {loading ? (
          <div className="button__spinner" aria-hidden="true">
            <Spinner name={'ball-clip-rotate'} color={'#fff'} fadeIn="none" />
          </div>
        ) : (
          <div className="button__content">
            {icon && <Icon color={disabled ? 'default' : iconColor} name={icon} size={iconSize} />}
            {!onlyIcon && <div className="button__text">{text}</div>}
          </div>
        )}
        {children && children}
      </>
    );

    const commonProps = {
      'aria-busy': loading,
      'aria-controls': ariaControls,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-label': buttonAriaLabel,
      'aria-live': 'polite',
      disabled: isDisabled,
      onClick,
      onKeyDown: handleKeyDown,
      title: titleProp,
    };

    const linkElement = (
      <Link
        ref={ref}
        to={to}
        className={accessibilityOnly ? buttonClassesAccessibility : buttonClasses}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        {...commonProps}
        {...restProps}
      >
        {renderContent()}
      </Link>
    );

    const buttonElement = (
      <button
        ref={ref}
        data-testid={testid}
        className={accessibilityOnly ? buttonClassesAccessibility : buttonClasses}
        type={type}
        {...commonProps}
        {...restProps}
      >
        {renderContent()}
      </button>
    );

    // Si tiene 'to', renderizar como enlace
    if (to) {
      return linkElement;
    }

    // Si no tiene 'to', renderizar como botón
    return buttonElement;
  }
);

Button.propTypes = {
  text: PropTypes.string,
  icon: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  variant: PropTypes.oneOf(['default', 'inline']),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small', 'xs', 'large', 'medium']),
  color: PropTypes.oneOf([
    'primary',
    'transparent',
    'transparent-primary',
    'transparent-light',
    'border',
    'white',
    'grey',
    'grey-secondary',
    'orange',
    'primary-outlined',
    'black',
    'alpha',
  ]),
  iconPosition: PropTypes.oneOf(['default', 'left', 'right']),
  iconColor: PropTypes.oneOf(['default', 'white', 'primary', 'gradient', 'grey', 'grey-light']),
  iconSize: PropTypes.oneOf(['xs', 'default', 'big', 'xl', 'xxl']),
  borderRadius: PropTypes.oneOf(['default', 'big', 'small', 'circle']),
  theme: PropTypes.oneOf(['default', 'google']),
  loading: PropTypes.bool,
  testid: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  to: PropTypes.string,
  onlyIcon: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  loadingText: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaControls: PropTypes.string,
  ariaHasPopup: PropTypes.oneOf(['dialog', 'menu', 'listbox', 'tree', 'grid']),
  accessibilityOnly: PropTypes.bool,
  onlyContent: PropTypes.bool,
  children: PropTypes.node,
};

// Añadir displayName para el componente forwardRef
Button.displayName = 'Button';

export default Button;
